import { JobType, useAIVisualsResult, useDeclutterProcess, useJobPoll } from 'dataQueries'

import { JobStatus } from 'constants/API'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { logger } from 'utils/logging'
import { PhotoStudioGenerationStepId } from '../generationSteps'

interface UseDeclutterProcessorProps {
  assignmentId: string
  sourceVisualFilename: string
  getLatestPreviewFilename: () => string | null
  onProgressUpdate?: (step: string) => void
  onComplete?: (resultUrl: string) => void
  onError?: (error: any) => void
}

/**
 * Reusable Declutter processor hook
 * Handles the complete flow from processing to result display
 */
export const useDeclutterProcessor = ({
  assignmentId,
  sourceVisualFilename,
  getLatestPreviewFilename,
  onProgressUpdate,
  onComplete,
  onError
}: UseDeclutterProcessorProps) => {
  const declutterProcess = useDeclutterProcess()
  const declutterJobPoll = useJobPoll(JobType.DECLUTTER)
  const declutterResult = useAIVisualsResult()
  const { t } = useTranslation(['photo_studio_editing'])

  const processDeclutter = useCallback(async (
    previousJobId?: string
  ) => {
    let lastJobStatus: string | undefined
    let lastJobError: string | undefined
    try {
      onProgressUpdate?.(PhotoStudioGenerationStepId.START_PROCESSING)

      // Step 1: Start Declutter processing
      const currentPreview = getLatestPreviewFilename()
      const inputVisualFilename = currentPreview || sourceVisualFilename

      const processResponse = await declutterProcess.mutateAsync({
        assignmentId,
        visualFilename: inputVisualFilename,
      })

      onProgressUpdate?.(PhotoStudioGenerationStepId.UPLOAD_MASK)

      // Step 2: Poll job until completion
      const jobResult = await declutterJobPoll.mutateAsync({
        jobId: processResponse.id
      })

      lastJobStatus = jobResult.status
      lastJobError = jobResult.error

      if (jobResult.status === JobStatus.FINISHED) {
        onProgressUpdate?.(PhotoStudioGenerationStepId.PROCESSING_COMPLETE)

        // Step 3: Get the AI result
        const aiResult = await declutterResult.refetchWithParams(assignmentId, processResponse.id)

        if (aiResult.signedURL) {
          onComplete?.(aiResult.signedURL)
          return {
            success: true,
            resultUrl: aiResult.signedURL,
            jobId: processResponse.id
          }
        } else {
          throw new Error('No AI result available')
        }
      } else if (jobResult.status === JobStatus.FAILED) {
        // Throw error for failed Declutter job - toast will be shown by controller's onError callback
        throw new Error(t('virtual_staging_error'))
      } else {
        throw new Error(`Declutter job failed with status: ${jobResult.error}`)
      }
    } catch (error) {
      logger.error('Declutter: Processing failed', error, {
        context: {
          component: 'PhotoStudio',
          action: 'declutterProcess',
          metadata: {
            assignmentId,
            sourceVisualFilename,
            jobStatus: lastJobStatus,
            jobError: lastJobError,
          }
        }
      })
      onError?.(error)

      return {
        success: false,
        error
      }
    }
  }, [
    assignmentId,
    sourceVisualFilename,
    getLatestPreviewFilename,
    declutterProcess,
    declutterJobPoll,
    declutterResult,
    onProgressUpdate,
    onComplete,
    onError,
    t
  ])

  return {
    processDeclutter,
    isProcessing: declutterProcess.isPending || declutterJobPoll.isPending,
    error: declutterProcess.error || declutterJobPoll.error
  }
}
